import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { setToken } from "../lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INVITE_STORAGE_KEY = "sportly_invite_token";

export const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isRegister, setIsRegister] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [inviteToken, setInviteToken] = useState(searchParams.get("invite") || "");
  const [inviteInfo, setInviteInfo] = useState(null);
  const [deniedInfo, setDeniedInfo] = useState(null);
  const [pendingVerification, setPendingVerification] = useState(null); // email string
  const [resending, setResending] = useState(false);

  useEffect(() => {
    // Handle ?denied=1 from AuthCallback → show the "vraag toegang" banner immediately
    if (searchParams.get("denied") === "1") {
      setDeniedInfo({
        message:
          "Geen uitnodiging gevonden voor dit e-mailadres. Vraag toegang via info@getsportly.com",
      });
    }
    // If ?invite= is on the URL, persist it (survives Google OAuth round-trip)
    // and fetch invite metadata so we can show a helpful welcome banner.
    const urlToken = searchParams.get("invite");
    const stored = sessionStorage.getItem(INVITE_STORAGE_KEY);
    const token = urlToken || stored || "";
    if (token) {
      sessionStorage.setItem(INVITE_STORAGE_KEY, token);
      setInviteToken(token);
      setIsRegister(true); // invite → assume they need to register
      axios
        .get(`${API}/auth/invite-info/${token}`)
        .then((r) => {
          setInviteInfo(r.data);
          setForm((f) => ({ ...f, email: r.data.email || f.email }));
        })
        .catch(() => {
          toast.error("Uitnodiging is ongeldig of verlopen. Vraag opnieuw toegang.");
          sessionStorage.removeItem(INVITE_STORAGE_KEY);
          setInviteToken("");
        });
    }
  }, [searchParams]);

  useEffect(() => {
    // Listen for token sent from popup via postMessage (cross-origin safe)
    const onMessage = (e) => {
      if (e.data?.type === "auth_success" && e.data?.token) {
        setToken(e.data.token);
        navigate("/", { replace: true });
      }
    };
    window.addEventListener("message", onMessage);

    // Also listen for localStorage changes (same-origin fallback)
    const onStorage = (e) => {
      if (e.key === "hp_session_token" && e.newValue) {
        navigate("/", { replace: true });
      }
    };
    window.addEventListener("storage", onStorage);

    return () => {
      window.removeEventListener("message", onMessage);
      window.removeEventListener("storage", onStorage);
    };
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setDeniedInfo(null);
    try {
      if (isRegister) {
        if (!form.name.trim()) {
          toast.error("Vul je naam in");
          setLoading(false);
          return;
        }
        const res = await axios.post(`${API}/auth/register`, { ...form, invite_token: inviteToken || null, app_url: window.location.origin });
        if (res.data?.status === "pending_verification") {
          sessionStorage.removeItem(INVITE_STORAGE_KEY);
          setPendingVerification(res.data.email || form.email);
          toast.success("Verificatiemail verstuurd — check je inbox");
          setLoading(false);
          return;
        }
        setToken(res.data.session_token);
        sessionStorage.removeItem(INVITE_STORAGE_KEY);
        toast.success("Account aangemaakt!");
      } else {
        const res = await axios.post(`${API}/auth/login`, { email: form.email, password: form.password });
        setToken(res.data.session_token);
      }
      navigate("/", { replace: true });
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || "Inloggen mislukt";
      if (status === 403) {
        setDeniedInfo({ message: detail });
      } else if (status === 429) {
        toast.error("Te veel pogingen. Probeer het over een minuut opnieuw.");
      } else {
        toast.error(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    const authUrl = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    // Full page redirect — playbook-compliant. Popups break on custom
    // production domains (COOP blocks window.opener → popup closes silently).
    window.location.href = authUrl;
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      data-testid="login-page"
      style={{ background: "var(--chalk-50, #F7F5EE)", fontFamily: "var(--sportly-font-body, system-ui)" }}
    >
      <Card className="w-full max-w-md shadow-lg border-slate-200">
        <CardHeader className="text-center space-y-3">
          <a href="/" className="flex items-center justify-center gap-2 no-underline text-inherit" data-testid="login-logo-link">
            <div
              style={{
                width: 34,
                height: 34,
                borderRadius: 8,
                background: "var(--pitch-600, #0E7C3A)",
                color: "#F7F5EE",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontFamily: "var(--sportly-font-display, serif)",
                fontWeight: 900,
                fontSize: 22,
              }}
            >
              S
            </div>
            <span
              style={{
                fontFamily: "var(--sportly-font-display, serif)",
                fontWeight: 900,
                fontSize: 22,
                textTransform: "uppercase",
                letterSpacing: "-0.01em",
              }}
            >
              Sportly
            </span>
          </a>
          <CardTitle
            className="text-2xl font-bold"
            data-testid="login-title"
            style={{ fontFamily: "var(--sportly-font-display, serif)" }}
          >
            {isRegister ? "Account aanmaken" : "Inloggen"}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {pendingVerification && (
            <div
              className="rounded-md border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900 text-center"
              data-testid="login-check-inbox-banner"
            >
              <p className="font-semibold mb-1">Check je inbox</p>
              <p className="text-xs leading-relaxed mb-3">
                We hebben een verificatielink verstuurd naar <strong>{pendingVerification}</strong>.
                Klik op de link om je account af te ronden.
              </p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={resending}
                onClick={async () => {
                  setResending(true);
                  try {
                    await axios.post(`${API}/auth/resend-verification`, { email: pendingVerification, app_url: window.location.origin });
                    toast.success("Verificatiemail opnieuw verstuurd");
                  } catch {
                    toast.error("Opnieuw versturen mislukt");
                  } finally {
                    setResending(false);
                  }
                }}
                data-testid="login-resend-verification-btn"
              >
                {resending ? "Versturen..." : "Opnieuw versturen"}
              </Button>
            </div>
          )}
          {inviteInfo && !pendingVerification && (
            <div
              className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-900"
              data-testid="invite-banner"
            >
              <p className="font-semibold">Je bent uitgenodigd!</p>
              <p className="mt-1 leading-relaxed">
                {inviteInfo.inviter_name || "Een manager"} heeft je uitgenodigd voor{" "}
                <strong>{inviteInfo.club_name} {inviteInfo.team_name}</strong>. Maak een account aan met{" "}
                <strong>{inviteInfo.email}</strong> om toegang te krijgen.
              </p>
            </div>
          )}
          {deniedInfo && (
            <div
              className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900"
              data-testid="access-denied-banner"
            >
              <p className="font-semibold">Geen toegang</p>
              <p className="mt-1 leading-relaxed">{deniedInfo.message}</p>
              <a
                href="mailto:info@getsportly.com?subject=Toegang%20aanvragen%20Sportly%20Hockey%20Planner"
                className="mt-2 inline-block font-medium text-amber-900 underline hover:text-amber-950"
                data-testid="request-access-link"
              >
                Toegang aanvragen via info@getsportly.com →
              </a>
            </div>
          )}
          {!pendingVerification && (
          <>
          <Button
            variant="outline"
            className="w-full h-10"
            onClick={handleGoogleLogin}
            data-testid="google-login-btn"
          >
            <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            Inloggen met Google
          </Button>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-slate-200" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white px-2 text-slate-400">of</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-3">
            {isRegister && (
              <div className="space-y-1">
                <Label htmlFor="name">Naam</Label>
                <Input
                  id="name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Jouw naam"
                  data-testid="register-name-input"
                />
              </div>
            )}
            <div className="space-y-1">
              <Label htmlFor="email">E-mail</Label>
              <Input
                id="email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="naam@club.nl"
                required
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="password">Wachtwoord</Label>
              <Input
                id="password"
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="Wachtwoord"
                required
                data-testid="login-password-input"
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-slate-900 hover:bg-slate-800"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? "Even geduld..." : isRegister ? "Registreren" : "Inloggen"}
            </Button>
          </form>

          <p className="text-center text-sm text-slate-500">
            {isRegister ? "Al een account?" : "Nog geen account?"}{" "}
            {inviteToken ? (
              // Invite-based flow — keep the local toggle so users can still switch back to login
              <button
                className="text-slate-900 font-medium hover:underline"
                onClick={() => setIsRegister(!isRegister)}
                data-testid="toggle-auth-mode"
              >
                {isRegister ? "Inloggen" : "Registreren"}
              </button>
            ) : isRegister ? (
              <button
                className="text-slate-900 font-medium hover:underline"
                onClick={() => setIsRegister(false)}
                data-testid="toggle-auth-mode"
              >
                Inloggen
              </button>
            ) : (
              <button
                className="text-slate-900 font-medium hover:underline"
                onClick={() => navigate("/abonnementen")}
                data-testid="toggle-auth-mode"
              >
                Registreren
              </button>
            )}
          </p>
          </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
